import React, { useState } from "react";
import PageHero from "../components/PageHero";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Popover, PopoverTrigger, PopoverContent } from "../components/ui/popover";
import { Calendar } from "../components/ui/calendar";
import { format } from "date-fns";
import { CalendarIcon, Send, CheckCircle2, Handshake, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import {
  phonePrefixes,
  countriesList,
  heroImages,
  supplierProductsOffered,
  supplierCategoryOptions,
  paymentTermsOptions,
} from "../mock";
import SEO from "../components/SEO";

export default function SupplierCenter() {
  const initial = {
    title: "Mr.",
    name: "",
    prefix: "+971",
    mobile: "",
    email: "",
    company: "",
    designation: "",
    website: "",
    category: "",
    sellingCountries: "",
    origin: "",
    moq: "",
    loadingPort: "",
    loadingCountry: "",
    payment: "",
  };
  const [form, setForm] = useState(initial);
  const [products, setProducts] = useState([]);
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [laycanStart, setLaycanStart] = useState(null);
  const [laycanEnd, setLaycanEnd] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e?.target ? e.target.value : e });

  const toggleProduct = (p) => {
    setProducts((prev) => (prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]));
  };

  const validateMobile = (val) => /^[0-9\s\-()]{6,}$/.test(val);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const MAX = 5 * 1024 * 1024;
    const allowed = ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"];
    if (f.size > MAX) {
      setFileError("File exceeds 5 MB. Please choose a smaller file.");
      setFile(null);
      return;
    }
    if (!allowed.includes(f.type) && !/\.(pdf|doc|docx)$/i.test(f.name)) {
      setFileError("Only PDF or DOC / DOCX files are allowed.");
      setFile(null);
      return;
    }
    setFileError("");
    setFile(f);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.mobile || !form.company) {
      toast.error("Please fill in Name, Mobile and Company (mandatory).");
      return;
    }
    if (!validateMobile(form.mobile)) {
      toast.error("Please enter a valid mobile number (6+ digits, digits only).");
      return;
    }
    if (products.length === 0) {
      toast.error("Please select at least one product from 'Products Offered'.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        company_name: form.company,
        contact_name: form.name,
        email: form.email,
        phone: `${form.prefix || ""} ${form.mobile}`.trim(),
        country: form.country,
        products,
        website: form.website || undefined,
        message: [
          form.category ? `Category: ${form.category}` : "",
          form.paymentTerms ? `Payment terms: ${form.paymentTerms}` : "",
          laycanStart ? `Laycan start: ${laycanStart}` : "",
          laycanEnd ? `Laycan end: ${laycanEnd}` : "",
          file?.name ? `Attached filename: ${file.name}` : "",
          form.notes || "",
        ].filter(Boolean).join(" | "),
      };
      await api.post("/inquiries/supplier", payload);
      setDone(true);
      toast.success("Registration submitted. Our sourcing desk will be in touch within 2–3 business days.");
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setSubmitting(false);
  };

  return (
    <>
      <SEO
        title="Supplier Center — Register as a Supplier"
        description="Register as a supplier or manufacturing partner with Ruken Alazdehar General Trading LLC. Quarry owners, manufacturers, traders and wholesalers welcome for long-term offtake agreements."
        path="/supplier-center"
      />
      <PageHero
        title="Supplier Center"
        subtitle="Become a supplier or manufacturing partner. Register your company and our sourcing team will reach out to discuss long-term agreements."
        breadcrumbs={[{ label: "Supplier Center" }]}
        bgImage={heroImages.contact}
      />

      <section className="section-padding bg-white">
        <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-3 gap-10">
          <aside className="lg:col-span-1 space-y-6">
            <div className="p-7 rounded-xl text-white" style={{ background: "linear-gradient(135deg, var(--brand-700) 0%, var(--brand-500) 100%)" }}>
              <Handshake size={32} className="mb-3" />
              <h3 className="font-display text-xl font-bold mb-2">Partner with Ruken Alazdehar</h3>
              <p className="text-white/85 text-sm">Multi-year offtake agreements, transparent pricing, and a strong global distribution network.</p>
            </div>
            <div className="p-7 rounded-xl border border-border bg-muted/30">
              <h4 className="font-display font-semibold mb-3" style={{ color: "var(--brand-700)" }}>Who can register?</h4>
              <ul className="text-sm text-muted-foreground space-y-2">
                {supplierCategoryOptions.map((c) => (
                  <li key={c} className="flex items-start gap-2">
                    <CheckCircle2 size={15} style={{ color: "var(--brand-500)" }} className="mt-0.5" /> {c}
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <div className="lg:col-span-2">
            {done ? (
              <div className="bg-white rounded-xl border border-border p-10 text-center">
                <CheckCircle2 size={56} className="mx-auto mb-4" style={{ color: "var(--brand-500)" }} />
                <h3 className="font-display text-2xl font-bold mb-2" style={{ color: "var(--brand-700)" }}>Thank you for registering!</h3>
                <p className="text-muted-foreground">Our sourcing team will review your submission and reach out within 2–3 business days.</p>
                <Button onClick={() => { setDone(false); setForm(initial); setProducts([]); setFile(null); }} className="mt-6 text-white" style={{ backgroundColor: "var(--brand-700)" }}>Submit another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-xl border border-border p-7 md:p-9 shadow-sm">
                <div className="mb-6">
                  <p className="text-xs uppercase tracking-[0.3em] font-semibold" style={{ color: "var(--brand-500)" }}>Supplier Registration</p>
                  <h2 className="font-display text-2xl md:text-3xl font-bold mt-2" style={{ color: "var(--brand-700)" }}>Tell us about your company</h2>
                </div>
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Title *</Label>
                    <Select value={form.title} onValueChange={set("title")}>
                      <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="Mr.">Mr.</SelectItem><SelectItem value="Ms.">Ms.</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Full Name *</Label>
                    <Input className="mt-1.5" value={form.name} onChange={set("name")} required />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Mobile *</Label>
                    <div className="mt-1.5 flex gap-2">
                      <Select value={form.prefix} onValueChange={set("prefix")}>
                        <SelectTrigger className="w-[105px]"><SelectValue /></SelectTrigger>
                        <SelectContent className="max-h-72">{phonePrefixes.map((p) => <SelectItem key={p.code} value={p.code}>{p.code} {p.country}</SelectItem>)}</SelectContent>
                      </Select>
                      <Input
                        className="flex-1"
                        value={form.mobile}
                        onChange={(e) => setForm({ ...form, mobile: e.target.value.replace(/[^\d\s\-()]/g, "") })}
                        pattern="[0-9\s\-()]{6,}"
                        placeholder="56 389 8675"
                        required
                      />
                    </div>
                    {form.mobile && !validateMobile(form.mobile) && (
                      <p className="text-xs text-destructive mt-1">Enter at least 6 digits (numbers only).</p>
                    )}
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Email</Label>
                    <Input className="mt-1.5" type="email" value={form.email} onChange={set("email")} placeholder="you@company.com" />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Manufacturing / Supplying Company *</Label>
                    <Input className="mt-1.5" value={form.company} onChange={set("company")} required />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Designation</Label>
                    <Input className="mt-1.5" value={form.designation} onChange={set("designation")} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Website</Label>
                    <Input className="mt-1.5" type="url" value={form.website} onChange={set("website")} placeholder="https://" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Category *</Label>
                    <Select value={form.category} onValueChange={set("category")}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select category" /></SelectTrigger>
                      <SelectContent>
                        {supplierCategoryOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Existing Selling Countries</Label>
                    <Input className="mt-1.5" value={form.sellingCountries} onChange={set("sellingCountries")} placeholder="e.g. India, China, UAE" />
                  </div>

                  {/* Products Offered (multi-select) */}
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Products Offered * (multi-select)</Label>
                    <div className="mt-2 grid sm:grid-cols-2 gap-2 p-4 rounded-lg border border-border bg-muted/20">
                      {supplierProductsOffered.map((p) => {
                        const checked = products.includes(p);
                        const id = `prod-${p.replace(/[^a-z0-9]/gi, "-")}`;
                        return (
                          <label
                            key={p}
                            htmlFor={id}
                            className={`flex items-center gap-2.5 px-3 py-2 rounded-md cursor-pointer text-sm transition ${
                              checked ? "bg-white border border-[var(--brand-500)]" : "hover:bg-white/60"
                            }`}
                          >
                            <Checkbox id={id} checked={checked} onCheckedChange={() => toggleProduct(p)} />
                            <span className={checked ? "font-medium" : "text-foreground/80"} style={checked ? { color: "var(--brand-700)" } : {}}>
                              {p}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                    {products.length > 0 && (
                      <p className="text-xs text-muted-foreground mt-2">
                        {products.length} selected
                      </p>
                    )}
                  </div>

                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Product Origin</Label>
                    <Select value={form.origin} onValueChange={set("origin")}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent className="max-h-72">{countriesList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">MOQ (tons)</Label>
                    <Input className="mt-1.5" value={form.moq} onChange={set("moq")} placeholder="e.g. 5000" />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Loading Port</Label>
                    <Input className="mt-1.5" value={form.loadingPort} onChange={set("loadingPort")} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Loading Country</Label>
                    <Select value={form.loadingCountry} onValueChange={set("loadingCountry")}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select country" /></SelectTrigger>
                      <SelectContent className="max-h-72">{countriesList.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Preferred Payment Terms</Label>
                    <Select value={form.payment} onValueChange={set("payment")}>
                      <SelectTrigger className="mt-1.5"><SelectValue placeholder="Select payment terms" /></SelectTrigger>
                      <SelectContent>{paymentTermsOptions.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Next Available Laycan — Start</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="mt-1.5 w-full justify-start text-left font-normal">
                          <CalendarIcon size={14} className="mr-2" />
                          {laycanStart ? format(laycanStart, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={laycanStart} onSelect={setLaycanStart} />
                      </PopoverContent>
                    </Popover>
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Next Available Laycan — End</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button type="button" variant="outline" className="mt-1.5 w-full justify-start text-left font-normal">
                          <CalendarIcon size={14} className="mr-2" />
                          {laycanEnd ? format(laycanEnd, "PPP") : "Pick a date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <Calendar mode="single" selected={laycanEnd} onSelect={setLaycanEnd} />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* File Upload */}
                  <div className="md:col-span-2">
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">
                      Upload Company Profile / Product Specification / COA (PDF or DOC, Max 5 MB)
                    </Label>
                    <label
                      htmlFor="supplier-file"
                      className="mt-1.5 flex items-center gap-3 px-4 py-4 rounded-lg border-2 border-dashed border-border hover:border-[var(--brand-500)] bg-muted/20 cursor-pointer transition"
                    >
                      <Upload size={20} style={{ color: "var(--brand-500)" }} />
                      <div className="flex-1">
                        {file ? (
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground">{file.name}</span>
                            <span className="text-xs text-muted-foreground">({(file.size / 1024 / 1024).toFixed(2)} MB)</span>
                            <button
                              type="button"
                              onClick={(e) => { e.preventDefault(); setFile(null); setFileError(""); }}
                              className="ml-auto text-muted-foreground hover:text-destructive"
                              aria-label="Remove file"
                            >
                              <X size={15} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <p className="text-sm font-medium text-foreground">Click to upload or drag & drop</p>
                            <p className="text-xs text-muted-foreground">PDF, DOC or DOCX (Max 5 MB)</p>
                          </>
                        )}
                      </div>
                      <input
                        id="supplier-file"
                        type="file"
                        accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        onChange={onFile}
                        className="hidden"
                      />
                    </label>
                    {fileError && <p className="text-xs text-destructive mt-1">{fileError}</p>}
                  </div>
                </div>

                <Button type="submit" disabled={submitting} className="mt-7 w-full md:w-auto text-white rounded-md h-11 px-7" style={{ backgroundColor: "var(--brand-700)" }}>
                  {submitting ? "Submitting..." : (<span className="inline-flex items-center gap-2">Register as Supplier <Send size={15} /></span>)}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
